/**
 * Shared class tokens for the preview kit.
 *
 * These exist so a recurring decision is made ONCE instead of being re-typed
 * per component and drifting. Each token is a plain `tw:`-prefixed class string,
 * so it composes with `cn()` and tailwind-merge resolves conflicts normally:
 *
 *   <p className={HELP_TEXT}>…</p>
 *   <Input className={cn(TAP_TARGET, "tw:pl-8")} />
 *   <Button className={cn(TAP_TARGET, isBusy && "tw:opacity-50")} />
 *
 * WHY CLASS STRINGS AND NOT CSS UTILITIES — a `@utility help-text` in
 * globals.css would work, but this project's Tailwind runs in prefix mode with a
 * scoped `.tw-root` preflight, and custom utilities interact with `@layer` in
 * ways that have already bitten the `.ds-icon` rules (see CLAUDE.md). Plain
 * strings have no build-time behaviour to get wrong.
 *
 * HANDOFF — a component importing from here depends on this file existing in
 * production too, the same way it depends on `@/lib/utils` for `cn`. Either port
 * this file across with the first component that uses it, or inline the token
 * values at merge time. Prod stays the source of truth: if prod already has an
 * equivalent token, make THIS file match it rather than the other way round.
 *
 * Adding a token: only promote a value once at least two components need it and
 * the two must not drift apart. A one-off stays local to its component.
 */

/**
 * De-emphasised secondary text. Two names, deliberately one value: they should
 * never drift apart visually, but the call sites read very differently, and the
 * distinction is what keeps the `text-sm` boundary below enforceable.
 */
const MUTED_SECONDARY = "tw:text-xs tw:text-muted-foreground"

/**
 * Helper/hint text under a control — one step lighter than the `text-sm` label
 * above it, which is what makes label · control · hint read as a hierarchy
 * rather than three near-identical lines.
 *
 * Use for hints ATTACHED TO A CONTROL. Not for empty-state or error body copy,
 * which stays `text-sm` — shrinking those makes failure states harder to read at
 * exactly the moment the user needs them.
 */
export const HELP_TEXT = MUTED_SECONDARY

/**
 * Informative metadata: captions, counts, timestamps, "+26 more". Text that
 * reports something rather than instructing or affording an action.
 *
 * Same weight as HELP_TEXT on purpose. The reason to reach for this name instead
 * is that it marks the text as passive — a count styled like a control invites a
 * tap that isn't there.
 */
export const META_TEXT = MUTED_SECONDARY

/**
 * Mobile tap-target height for controls whose primitive defaults to `h-8`
 * (32px), restoring the primitive at `md`. 44px is the iOS HIG minimum.
 *
 * Applies to Input, Button and toggle items alike, so the same control can't end
 * up 44px on one step of a flow and 32px on the next. tailwind-merge drops the
 * primitive's own `h-8` in favour of `h-11` and keeps the `md:` restoration, so
 * desktop computes exactly as the primitive intended.
 */
export const TAP_TARGET = "tw:h-11 tw:md:h-8"
